import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, LogOut, ArrowLeft, Award, Users, Eye, X, 
  Filter, ChevronDown, Crown, UserMinus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminGrandfatherMembers() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    role: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 25;

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) fetchMembers();
  }, [filters, offset, user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      setUser(response.data);
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.role !== 'all') params.append('role', filters.role);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/grandfather-members?${params.toString()}`,
        { withCredentials: true }
      );
      setMembers(response.data.members);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (userId, userName) => {
    if (!confirm(`Are you sure you want to revoke grandfather status from ${userName}?`)) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${userId}/revoke-grandfather`,
        {},
        { withCredentials: true }
      );
      toast.success('Grandfather status revoked');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke status');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      navigate('/login');
    }
  };

  if (loading && !members.length) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Grandfather Members</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user?.name}</span>
            <button onClick={handleLogout} className="btn-secondary text-sm flex items-center gap-2" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Crown className="h-8 w-8 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{total}</p>
                <p className="text-sm text-slate-600">Founding Members</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Filters</h3>
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className="btn-secondary text-sm flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? 'Hide' : 'Show'} Filters
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">User Type</label>
                <select 
                  value={filters.role}
                  onChange={(e) => setFilters({...filters, role: e.target.value})}
                  className="w-full"
                >
                  <option value="all">All Types</option>
                  <option value="tradesperson">Tradespeople</option>
                  <option value="employer">Employers</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Members List */}
        <div className="card">
          <h3 className="text-lg font-bold text-slate-900 mb-4">
            <Award className="h-5 w-5 inline-block mr-2 text-amber-500" />
            Founding Members ({total})
          </h3>

          {members.length === 0 ? (
            <div className="text-center py-12">
              <Crown className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">No founding members yet</p>
              <p className="text-slate-500 text-sm">Users will appear here when granted grandfather status</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Member</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Badge</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Referrals</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Granted</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr 
                      key={member.user_id} 
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`member-row-${member.user_id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{member.name}</p>
                            <p className="text-xs text-slate-500">{member.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          member.user_type === 'tradesperson' 
                            ? 'bg-orange-100 text-orange-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {member.user_type}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 text-xs font-medium">
                          <Crown className="h-3 w-3" />
                          {member.grandfather_label}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          <p className="text-slate-900 font-medium">{member.referral_counts?.total || 0} total</p>
                          <p className="text-xs text-slate-500">
                            {member.referral_counts?.trade || 0} trades, {member.referral_counts?.employer || 0} employers
                          </p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {member.grandfather_granted_at 
                          ? new Date(member.grandfather_granted_at).toLocaleDateString()
                          : 'N/A'
                        }
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button 
                            onClick={() => navigate(`/admin/user/${member.user_id}`)}
                            className="text-slate-600 hover:text-slate-700 p-1"
                            title="View Profile"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleRevoke(member.user_id, member.name)}
                            className="text-red-600 hover:text-red-700 p-1"
                            title="Revoke Status"
                          >
                            <UserMinus className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button 
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="btn-secondary text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button 
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                  className="btn-secondary text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
